import {isEqual} from 'lodash';
import {set} from 'lodash/fp';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {CallToAction} from '@contentlab/api/avatars/types';
import type {ScriptConfiguration} from '@contentlab/api/avatars/types';
import AccordionSection from '@contentlab/components/accordion-section';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewTransitionEvent from '@contentlab/hooks/analytics/use-view-transition-event';
import ConfigMenu from '@contentlab/views/contentlab/pages/ai-avatar-page/components/config-menu/ConfigMenu';
import {DEFAULT_SCRIPT_CONFIG} from '@contentlab/views/contentlab/pages/ai-avatar-page/constants';
import {isCustomGoal} from '@contentlab/views/contentlab/pages/ai-avatar-page/constants/goalOptions';
import {TONE_OPTIONS} from '@contentlab/views/contentlab/pages/ai-avatar-page/constants/toneOptions';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import ProjectBasicsSection, {
  AudienceChannelsSection,
  CreativeStyleSection,
} from '../sections';

enum ScriptConfigMenuSection {
  PROJECT_BASICS = 'projectBasics',
  AUDIENCE_CHANNELS = 'audienceChannels',
  CREATIVE_STYLE = 'creativeStyle',
}

interface ScriptConfigMenuProps {
  testID?: string;
  onClose?: () => void;
  scriptConfig: ScriptConfiguration;
  onScriptConfigChange: (config: Partial<ScriptConfiguration>) => void;
  onApply: (config: ScriptConfiguration) => Promise<void>;
  onConfigDirty?: (isDirty: boolean) => void;
}

function ScriptConfigMenu({
  testID = 'script-config-menu',
  onClose,
  scriptConfig,
  onScriptConfigChange,
  onApply,
  onConfigDirty,
}: ScriptConfigMenuProps) {
  const [isApplying, setIsApplying] = useState(false);

  const [localConfig, setLocalConfig] =
    useState<ScriptConfiguration>(scriptConfig);

  const [customGoalAnchorEl, setCustomGoalAnchorEl] =
    useState<HTMLElement | null>(null);

  const {uiInteraction} = useUiInteractionEvent();
  const viewTransition = useViewTransitionEvent();

  // Use ref to proxy scriptConfig and prevent infinite loops
  const scriptConfigStringRef = useRef<string>(JSON.stringify(scriptConfig));

  useEffect(() => {
    const presentationId = uuid();
    const startTime = Date.now();
    viewTransition.presented({
      presentation_id: presentationId,
      view_name: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_SCRIPT,
      view_type: 'view',
      view_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
      source: 'clicked Smart Edit',
    });

    return () => {
      const endTime = Date.now();
      viewTransition.dismissed({
        interaction_id: uuid(),
        presentation_id: presentationId,
        reason: 'closed by user',
        view_details: `Total presented time: ${endTime - startTime} ms`,
      });
    };
  }, []);

  useEffect(() => {
    // Only update when content actually changed (not just reference)
    const currentScriptConfigString = JSON.stringify(scriptConfig);
    if (scriptConfigStringRef.current !== currentScriptConfigString) {
      setLocalConfig(scriptConfig);
      scriptConfigStringRef.current = currentScriptConfigString;
    }
  }, [scriptConfig]);

  // Track whether config has been modified
  useEffect(() => {
    const parsedScriptConfig = JSON.parse(scriptConfigStringRef.current);
    const parsedLocalConfig = JSON.parse(JSON.stringify(localConfig));
    const isDirty = !isEqual(parsedLocalConfig, parsedScriptConfig);

    onConfigDirty?.(isDirty);
  }, [localConfig, onConfigDirty]);

  const logAnalyticsUpdate = (path: string, value: unknown) => {
    uiInteraction({
      item_name: 'customize_script_menu_field_update',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_SCRIPT,
      interaction_details: JSON.stringify({
        path,
        value,
      }),
    });
  };

  // Generic update function using lodash/fp set
  const updateLocalConfig = useCallback((path: string, value: unknown) => {
    setLocalConfig((prev) => set(path, value, prev));

    logAnalyticsUpdate(path, value);
  }, []);

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    [ScriptConfigMenuSection.PROJECT_BASICS]: true,
    [ScriptConfigMenuSection.AUDIENCE_CHANNELS]: false,
    [ScriptConfigMenuSection.CREATIVE_STYLE]: false,
  });

  const handleSectionChange = useCallback(
    // Collapse all sections except the one that is being expanded
    (section: string, expanded: boolean) => {
      setExpandedSections({
        [ScriptConfigMenuSection.PROJECT_BASICS]:
          section === ScriptConfigMenuSection.PROJECT_BASICS && expanded,
        [ScriptConfigMenuSection.AUDIENCE_CHANNELS]:
          section === ScriptConfigMenuSection.AUDIENCE_CHANNELS && expanded,
        [ScriptConfigMenuSection.CREATIVE_STYLE]:
          section === ScriptConfigMenuSection.CREATIVE_STYLE && expanded,
      });

      uiInteraction({
        item_name: 'customize_script_menu_section_toggle',
        interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_SCRIPT,
        interaction_details: JSON.stringify({
          section,
          expanded,
        }),
      });
    },
    []
  );

  const handleApply = useCallback(async () => {
    if (isApplying) {
      return;
    }
    try {
      setIsApplying(true);
      await onApply({
        ...localConfig,
        tone: localConfig.tone || TONE_OPTIONS[0].id, // fallback to default tone if no selection were made
      });
    } catch (e) {
      // TODO: Show error message to user
    } finally {
      setIsApplying(false);
    }

    uiInteraction({
      item_name: 'customize_script_menu_apply',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_SCRIPT,
      interaction_details: JSON.stringify({
        applied_config: localConfig,
      }),
    });
  }, [localConfig, onApply, isApplying]);

  const handleClearAll = useCallback(() => {
    setExpandedSections({
      [ScriptConfigMenuSection.PROJECT_BASICS]: true,
      [ScriptConfigMenuSection.AUDIENCE_CHANNELS]: false,
      [ScriptConfigMenuSection.CREATIVE_STYLE]: false,
    });

    setLocalConfig({...DEFAULT_SCRIPT_CONFIG});

    uiInteraction({
      item_name: 'customize_script_menu_clear',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR_CUSTOMIZE_SCRIPT,
    });
  }, []);

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      updateLocalConfig(field, value);
    },
    [updateLocalConfig]
  );

  const handleArrayToggle = useCallback(
    (field: string, value: string) => {
      // Get current value as array (convert single value to array if needed)
      const currentValue = localConfig[field as keyof ScriptConfiguration];
      const currentArray = currentValue ? [currentValue as string] : [];

      // Toggle value in array
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item) => item !== value)
        : [...currentArray, value];

      // Update config with last added value from array (or null if empty)
      updateLocalConfig(field, newArray.length > 0 ? newArray.pop() : null);
    },
    [localConfig, updateLocalConfig]
  );

  const handleCustomGoalAccept = useCallback(
    (customGoalText: string) => {
      updateLocalConfig('callToAction', customGoalText);
    },
    [updateLocalConfig]
  );

  const handleCustomGoalDelete = useCallback(() => {
    updateLocalConfig('callToAction', null);
  }, [updateLocalConfig]);

  const handleCustomGoalPopperOpen = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      if (event) {
        setCustomGoalAnchorEl(event.currentTarget);
      }
    },
    []
  );

  const handleCustomGoalPopperClose = useCallback(() => {
    setCustomGoalAnchorEl(null);
  }, []);

  return (
    <ConfigMenu
      testID={testID}
      title={translate('Smart Edit')}
      onClose={onClose}
      isDisabled={isApplying}
      topSectionButtons={[
        {
          label: translate('Clear all'),
          icon: 'Actions-Reset',
          appearance: 'neutral',
          mode: 'plain',
          onClick: handleClearAll,
        },
        {
          label: isApplying ? translate('Applying...') : translate('Apply'),
          appearance: 'neutral',
          mode: 'filled',
          isLoading: isApplying,
          onClick: handleApply,
          sx: {
            backgroundColor: '#000',
            color: '#fff',
            minWidth: '120px',
          },
        },
      ]}
      accordionSections={[
        <AccordionSection
          key={ScriptConfigMenuSection.PROJECT_BASICS}
          testID={ScriptConfigMenuSection.PROJECT_BASICS}
          sectionKey={ScriptConfigMenuSection.PROJECT_BASICS}
          title={translate('Project Basics')}
          iconName="Navigation-Megaphone"
          expanded={expandedSections.projectBasics}
          onToggle={handleSectionChange}
          variant="compact"
        >
          <ProjectBasicsSection
            testID="project-basics-form"
            promoting={localConfig.product || ''}
            onPromotingChange={(value) => handleFieldChange('product', value)}
            selectedGoal={(localConfig.callToAction as CallToAction) || null}
            onGoalSelect={(goal) => {
              handleFieldChange('callToAction', goal);
            }}
            customGoal={
              isCustomGoal(localConfig.callToAction)
                ? localConfig.callToAction
                : ''
            }
            onCustomGoalAccept={handleCustomGoalAccept}
            onCustomGoalDelete={handleCustomGoalDelete}
            onCustomGoalPopperOpen={handleCustomGoalPopperOpen}
            isCustomGoalPopperOpen={!!customGoalAnchorEl}
            onCustomGoalPopperClose={handleCustomGoalPopperClose}
            customGoalAnchorEl={customGoalAnchorEl}
          />
        </AccordionSection>,
        <AccordionSection
          key={ScriptConfigMenuSection.AUDIENCE_CHANNELS}
          testID={ScriptConfigMenuSection.AUDIENCE_CHANNELS}
          sectionKey={ScriptConfigMenuSection.AUDIENCE_CHANNELS}
          title={translate('Audience & Channels')}
          iconName="Features-Face-Mix"
          expanded={expandedSections.audienceChannels}
          onToggle={handleSectionChange}
          variant="compact"
        >
          <AudienceChannelsSection
            testID="audience-channels-form"
            selectedPlatforms={
              localConfig.targetPlatform ? [localConfig.targetPlatform] : []
            }
            onPlatformSelect={(platform) =>
              handleArrayToggle('targetPlatform', platform)
            }
            selectedAudiences={
              localConfig.targetAudience ? [localConfig.targetAudience] : []
            }
            onAudienceSelect={(audience) =>
              handleArrayToggle('targetAudience', audience)
            }
          />
        </AccordionSection>,
        <AccordionSection
          key={ScriptConfigMenuSection.CREATIVE_STYLE}
          testID={ScriptConfigMenuSection.CREATIVE_STYLE}
          sectionKey={ScriptConfigMenuSection.CREATIVE_STYLE}
          title={translate('Creative Style')}
          iconName="Features-PortraitLight"
          expanded={expandedSections.creativeStyle}
          onToggle={handleSectionChange}
          variant="compact"
        >
          <CreativeStyleSection
            testID="creative-style-form"
            toneValue={localConfig.tone || TONE_OPTIONS[0].id}
            onToneChange={(value) => handleFieldChange('tone', value)}
            selectedScriptTypes={
              localConfig.scriptType ? [localConfig.scriptType] : []
            }
            onScriptTypeSelect={(value) =>
              handleFieldChange('scriptType', value)
            }
            creativeDescription={localConfig.specialRequests || ''}
            onCreativeDescriptionChange={(value) =>
              handleFieldChange('specialRequests', value)
            }
          />
        </AccordionSection>,
      ]}
    />
  );
}

export default ScriptConfigMenu;
